import { getFunctions, httpsCallable } from 'firebase/functions';
import { isDemoMode } from '../demo/isDemoMode';

// Utility function to create a delay
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ─── Demo conversation engine ────────────────────────────────────────────────

const CONVERSATION_STAGES = [
  'eventType',   // What kind of event + name
  'flyer',       // Upload a flyer or skip
  'dateLocation',
  'vendorCapacity',
  'vendorTypes',
  'loadIn',
  'done',
];

function extractFieldsFromMessage(message, stage, currentFields) {
  const text = message.toLowerCase();
  const fields = { ...currentFields };

  // Event type + name
  const typeMap = {
    'farmers market': 'Farmers Market',
    'craft fair': 'Craft Fair',
    'artisan': 'Artisan Fest',
    'festival': 'Festival',
    'market': 'Farmers Market',
    'fair': 'Craft Fair',
    'pop.up': 'Pop-up Market',
    'flea': 'Flea Market',
    'food': 'Food Festival',
  };
  for (const [kw, type] of Object.entries(typeMap)) {
    if (new RegExp(kw).test(text)) {
      fields.eventType = type;
      if (!fields.eventName) fields.eventName = type;
      break;
    }
  }

  // Event name — grab quoted text or first capitalized phrase
  const quotedMatch = message.match(/[""]([^""]+)[""]/);
  if (quotedMatch) fields.eventName = quotedMatch[1];
  const calledMatch = message.match(/(?:called|named|it's|its)\s+([A-Z][^.,!?\n]{2,40})/);
  if (calledMatch) fields.eventName = calledMatch[1].trim();

  // Dates
  const months = {
    jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
    january: 1, february: 2, march: 3, april: 4, june: 6, july: 7, august: 8, september: 9, october: 10, november: 11, december: 12
  };
  const dateMatch = message.match(/(\w+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:\s*[-–]\s*(\d{1,2})(?:st|nd|rd|th)?)?/);
  if (dateMatch) {
    const month = months[dateMatch[1].toLowerCase()];
    if (month) {
      const year = new Date().getFullYear() + (month < new Date().getMonth() + 1 ? 1 : 0);
      const pad = (n) => String(n).padStart(2, '0');
      fields.startDate = `${year}-${pad(month)}-${pad(parseInt(dateMatch[2]))}`;
      fields.endDate = dateMatch[3]
        ? `${year}-${pad(month)}-${pad(parseInt(dateMatch[3]))}`
        : fields.startDate;
      fields.startTime = '09:00';
      fields.endTime = '18:00';
    }
  }

  // Location
  const locMatch = message.match(/(?:at|in|@)\s+([A-Z][^.,!?\n]{2,60})/);
  if (locMatch) fields.location = locMatch[1].trim();
  const locLower = message.match(/(?:at|in)\s+([a-z][^.,!?\n]{2,40})/);
  if (locLower && !fields.location) fields.location = locLower[1].trim();

  // Capacity
  const capMatch = text.match(/(\d+)\s*(?:vendors?|booths?|spots?|stalls?|sellers?)/);
  if (capMatch) fields.sellerCapacity = parseInt(capMatch[1]);
  const capMatch2 = text.match(/(?:up to|around|about|maybe|roughly)\s*(\d+)/);
  if (capMatch2 && !fields.sellerCapacity) fields.sellerCapacity = parseInt(capMatch2[1]);

  // Registration fee
  const feeMatch = text.match(/\$\s*(\d+(?:\.\d{2})?)/);
  if (feeMatch) fields.registrationFee = parseFloat(feeMatch[1]);
  const feeMatch2 = text.match(/(\d+)\s*(?:dollars?|per vendor|per booth|fee)/);
  if (feeMatch2 && !fields.registrationFee) fields.registrationFee = parseFloat(feeMatch2[1]);

  // Vendor types
  const vendorTypeMap = {
    'food': 'Food & Beverage', 'beverage': 'Food & Beverage', 'drink': 'Food & Beverage',
    'craft': 'Crafts', 'handmade': 'Crafts', 'artisan': 'Artisan Goods',
    'art': 'Art', 'painting': 'Art', 'jewelry': 'Jewelry',
    'plant': 'Plants & Flowers', 'flower': 'Plants & Flowers',
    'clothing': 'Clothing', 'fashion': 'Clothing', 'apparel': 'Clothing',
    'candle': 'Candles & Home', 'soap': 'Candles & Home',
    'produce': 'Farm Produce', 'farm': 'Farm Produce', 'vegetable': 'Farm Produce',
    'vintage': 'Vintage & Antiques', 'antique': 'Vintage & Antiques',
    'pet': 'Pet Products', 'book': 'Books & Media',
  };
  const found = [];
  for (const [kw, type] of Object.entries(vendorTypeMap)) {
    if (text.includes(kw) && !found.includes(type)) found.push(type);
  }
  if (found.length > 0) {
    fields.sellerTypes = [...new Set([...(fields.sellerTypes || []), ...found])];
  }

  // Load-in time
  const loadMatch = text.match(/(?:load.?in|set.?up|setup)\s+(?:at\s+)?(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (loadMatch) {
    let hr = parseInt(loadMatch[1]);
    const min = loadMatch[2] || '00';
    if (loadMatch[3]?.toLowerCase() === 'pm' && hr < 12) hr += 12;
    fields.loadInTime = `${String(hr).padStart(2, '0')}:${min}`;
    fields.loadInDate = fields.startDate || '';
  }

  return fields;
}

function getNextAIMessage(stage, fields, fromFlyer = false) {
  switch (stage) {
    case 'eventType':
      return `Let's build your event. What type of market or festival are you running, and what's it called?`;

    case 'flyer':
      return `Got it${fields.eventName ? ` — **${fields.eventName}**` : ''}. Do you have a flyer or graphic? Upload it and I'll pull the details automatically.`;

    case 'dateLocation':
      if (fromFlyer) return `Let me confirm a few details. When is it and where? (e.g. "June 14 at Riverside Park, Portland")`;
      return `When is it and where? (e.g. "June 14 at Riverside Park, Portland")`;

    case 'vendorCapacity':
      return `How many vendor spots, and what's the booth fee?`;

    case 'vendorTypes':
      return `What kinds of vendors do you want? (food, crafts, art, plants, jewelry — list them all)`;

    case 'loadIn':
      return `What time does vendor load-in start? Any website or social handles to include?`;

    case 'done':
      return `Here's what I've captured:\n\n${formatFieldsSummary(fields)}\n\nHit **Fill My Form** to apply it to your listing.`;

    default:
      return `Tell me more about your event.`;
  }
}

function formatFieldsSummary(fields) {
  const lines = [];
  if (fields.eventName) lines.push(`**${fields.eventName}** (${fields.eventType || 'Event'})`);
  if (fields.startDate) lines.push(`Date: ${fields.startDate}${fields.endDate && fields.endDate !== fields.startDate ? ` – ${fields.endDate}` : ''}`);
  if (fields.location) lines.push(`Location: ${fields.location}`);
  if (fields.sellerCapacity) lines.push(`Vendors: ${fields.sellerCapacity} spots`);
  if (fields.registrationFee) lines.push(`Booth fee: $${fields.registrationFee}`);
  if (fields.sellerTypes?.length) lines.push(`Vendor types: ${fields.sellerTypes.join(', ')}`);
  return lines.join('\n');
}

function countFilledFields(fields) {
  const key = ['eventName', 'eventType', 'startDate', 'location', 'sellerCapacity', 'registrationFee', 'sellerTypes'];
  return key.filter(k => fields[k] && (Array.isArray(fields[k]) ? fields[k].length > 0 : true)).length;
}

// ─── AIService ───────────────────────────────────────────────────────────────

const AIService = {
  /**
   * Conversational event extraction.
   * Takes the full conversation history + latest user message.
   * Returns { aiMessage, extractedFields, stage, isComplete }
   */
  async chatEventExtraction(userMessage, conversationHistory, currentFields = {}) {
    if (isDemoMode) {
      await delay(700 + Math.random() * 600);

      // Detect flyer upload
      const isFlyerUpload = userMessage.startsWith('(flyer:');
      // Detect "looks good" / "yes" / "all set" — user confirming the summary
      const isConfirming = /^(looks good|yes|all set|that'?s? (it|all|correct|right)|go ahead|proceed|fill it|done|perfect|great|sounds good|yep|yup|ok(ay)?|sure)[\s!.]*$/i.test(userMessage.trim());

      if (isFlyerUpload) {
        // Simulate Claude vision scan — return rich mock extraction
        const mockFields = {
          ...currentFields,
          eventName: currentFields.eventName || 'Riverside Farmers Market',
          eventType: currentFields.eventType || 'Farmers Market',
          startDate: currentFields.startDate || (() => {
            const d = new Date(); d.setDate(d.getDate() + 30);
            return d.toISOString().split('T')[0];
          })(),
          endDate: currentFields.endDate || (() => {
            const d = new Date(); d.setDate(d.getDate() + 30);
            return d.toISOString().split('T')[0];
          })(),
          startTime: currentFields.startTime || '09:00',
          endTime: currentFields.endTime || '17:00',
          location: currentFields.location || 'Riverside Park, Portland, OR',
          sellerCapacity: currentFields.sellerCapacity || 60,
          registrationFee: currentFields.registrationFee || 45,
          sellerTypes: currentFields.sellerTypes?.length ? currentFields.sellerTypes : ['Farm Produce', 'Food & Beverage', 'Crafts', 'Art'],
        };

        const missing = [];
        if (!mockFields.loadInTime) missing.push('load-in time');
        if (!mockFields.applicationDeadline) missing.push('application deadline');

        const summary = formatFieldsSummary(mockFields);
        const missingLine = missing.length
          ? `\n\nI couldn't find: **${missing.join(', ')}**. Want to add those?`
          : '\n\nLooks like I got everything. Want to add anything else, or should I fill your form?';

        return {
          success: true,
          aiMessage: `Here's what I pulled from your flyer:\n\n${summary}${missingLine}`,
          extractedFields: mockFields,
          stage: 'review',
          isComplete: false,
          isFlyerScan: true,
          filledCount: countFilledFields(mockFields),
        };
      }

      // If user is confirming after review → mark done
      if (isConfirming && currentFields.eventName) {
        return {
          success: true,
          aiMessage: `Got it. Hit **Fill My Form** to apply everything to your listing.`,
          extractedFields: currentFields,
          stage: 'done',
          isComplete: true,
          filledCount: countFilledFields(currentFields),
        };
      }

      // Normal conversation flow
      const stageIndex = Math.min(
        Math.floor(conversationHistory.filter(m => m.role === 'user').length),
        CONVERSATION_STAGES.length - 1
      );
      const stage = CONVERSATION_STAGES[stageIndex];
      const updatedFields = extractFieldsFromMessage(userMessage, stage, currentFields);

      const nextStageIndex = Math.min(stageIndex + 1, CONVERSATION_STAGES.length - 1);
      const nextStage = CONVERSATION_STAGES[nextStageIndex];

      // After extracting fields, if we have enough for a summary → show it
      const filled = countFilledFields(updatedFields);
      let aiMessage;
      let finalStage = nextStage;
      let isComplete = false;

      if (filled >= 5 && nextStage !== 'done') {
        // Enough to summarize — skip to review
        const summary = formatFieldsSummary(updatedFields);
        const allFields = ['eventName', 'eventType', 'startDate', 'location', 'sellerCapacity', 'registrationFee', 'sellerTypes'];
        const missing = allFields.filter(k => !updatedFields[k] || (Array.isArray(updatedFields[k]) && !updatedFields[k].length));
        const missingLine = missing.length
          ? `\n\nStill need: **${missing.map(k => ({ eventName: 'event name', eventType: 'event type', startDate: 'date', location: 'location', sellerCapacity: 'vendor count', registrationFee: 'booth fee', sellerTypes: 'vendor types' })[k]).join(', ')}**. Got those?`
          : `\n\nThat's everything. Want to add anything else, or should I fill the form?`;
        aiMessage = `Here's what I have so far:\n\n${summary}${missingLine}`;
        finalStage = 'review';
      } else if (nextStage === 'done') {
        const summary = formatFieldsSummary(updatedFields);
        aiMessage = `Here's what I captured:\n\n${summary}\n\nHit **Fill My Form** to apply it to your listing.`;
        isComplete = true;
      } else {
        aiMessage = getNextAIMessage(nextStage, updatedFields);
      }

      return {
        success: true,
        aiMessage,
        extractedFields: updatedFields,
        stage: finalStage,
        isComplete,
        filledCount: countFilledFields(updatedFields),
      };
    }

    // Production: call Cloud Function
    // const functions = getFunctions();
    // const chatEventExtractionFn = httpsCallable(functions, 'chatEventExtraction');
    // const result = await chatEventExtractionFn({ userMessage, conversationHistory, currentFields });
    // return result.data;
    throw new Error('AI features require demo mode or a configured Cloud Function.');
  },

  /**
   * Analyze an event flyer image using Claude Haiku vision.
   * In demo mode returns a mock extraction. In production calls analyzeEventFlyer Cloud Function.
   * @param {File} file - The image file to analyze
   * @returns {{ success, fields }}
   */
  async analyzeFlyer(file) {
    const toBase64 = (f) => new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = (e) => res(e.target.result.split(',')[1]);
      reader.onerror = rej;
      reader.readAsDataURL(f);
    });

    const apiKey = process.env.REACT_APP_ANTHROPIC_API_KEY;

    if (isDemoMode) {
      if (apiKey) {
        try {
          const imageBase64 = await toBase64(file);
          const mimeType = file.type || 'image/jpeg';
          const response = await fetch('https://api.anthropic.com/v1/messages', {
            method: 'POST',
            headers: {
              'x-api-key': apiKey,
              'anthropic-version': '2023-06-01',
              'anthropic-dangerous-direct-browser-access': 'true',
              'content-type': 'application/json',
            },
            body: JSON.stringify({
              model: 'claude-haiku-4-5-20251001',
              max_tokens: 1024,
              messages: [{
                role: 'user',
                content: [
                  {
                    type: 'image',
                    source: { type: 'base64', media_type: mimeType, data: imageBase64 },
                  },
                  {
                    type: 'text',
                    text: `You are extracting event details from a flyer image. Return ONLY valid JSON with these fields (omit any you can't find):
{
  "eventName": string,
  "eventType": string (e.g. "Farmers Market", "Festival", "Concert", "Pop-up Market"),
  "description": string,
  "startDate": "YYYY-MM-DD",
  "endDate": "YYYY-MM-DD",
  "startTime": "HH:MM",
  "endTime": "HH:MM",
  "location": string,
  "sellerCapacity": number,
  "registrationFee": number,
  "sellerTypes": string[],
  "website": string
}
Return only the JSON object, no explanation.`,
                  },
                ],
              }],
            }),
          });
          if (response.ok) {
            const data = await response.json();
            const text = data.content?.[0]?.text || '{}';
            const jsonMatch = text.match(/\{[\s\S]*\}/);
            const fields = jsonMatch ? JSON.parse(jsonMatch[0]) : {};
            return { success: true, fields };
          }
          // API error (e.g. no credits) — fall through to mock
        } catch (_) {
          // Network/parse error — fall through to mock
        }
      }

      // Fallback mock
      await delay(1800);
      const now = new Date();
      const startDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return {
        success: true,
        fields: {
          eventName: 'Riverside Farmers Market',
          eventType: 'Farmers Market',
          description: 'A vibrant outdoor market featuring local farmers, artisans, and food vendors.',
          startDate: startDate.toISOString().split('T')[0],
          endDate: startDate.toISOString().split('T')[0],
          startTime: '09:00',
          endTime: '17:00',
          location: 'Riverside Park, Portland, OR',
          sellerCapacity: 60,
          registrationFee: 45,
          sellerTypes: ['Farm Produce', 'Food & Beverage', 'Crafts', 'Art'],
        },
      };
    }

    // Production: encode file and call Cloud Function
    const imageBase64 = await toBase64(file);
    const mimeType = file.type || 'image/jpeg';
    const fns = getFunctions();
    const analyzeEventFlyer = httpsCallable(fns, 'analyzeEventFlyer');
    const result = await analyzeEventFlyer({ imageBase64, mimeType });
    return result.data;
  },

  /**
   * Generate a personalized vendor invite email.
   * Returns { subject, body }
   */
  async generateVendorInvite(vendorData, eventHistory = []) {
    const functions = getFunctions();
    const generateVendorInviteFn = httpsCallable(functions, 'generateVendorInvite');
    const result = await generateVendorInviteFn({ vendorData, eventHistory });
    return result.data;
  },

  async summarizeNotes(notes, vendorData) {
    const functions = getFunctions();
    const summarizeNotesFn = httpsCallable(functions, 'summarizeNotes');
    const result = await summarizeNotesFn({ notes, vendorData });
    return result.data.summary;
  },

  async suggestTags(vendors) {
    const functions = getFunctions();
    const suggestTagsFn = httpsCallable(functions, 'suggestTags');
    const result = await suggestTagsFn({ vendors });
    return result.data.suggestions;
  },

  async getSyncInsights(vendors) {
    const functions = getFunctions();
    const getSyncInsightsFn = httpsCallable(functions, 'getSyncInsights');
    const result = await getSyncInsightsFn({ vendors });
    return result.data.insights;
  },
};

export default AIService;
